import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { fetchSkipOutcomes } from '../../api/trades';
import { fmtR, getSkipReasonLabel, SKIP_STATUS_LABELS, skipChartTooltip, skipStatusLookup } from './reviewHelpers';
import DWLFLoader from '../../components/DWLFLoader';
import useSchemeColors from '../../hooks/useSchemeColors';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend, Filler);

/*
 * Counterfactual — the trades you didn't take.
 *
 * Every skipped signal carries its decision-time-frozen entry/SL/TP, so the
 * backend can replay "what if I'd taken it" mechanically (stop-first /
 * target-first / running) with zero hindsight bias. This page renders that
 * scorecard: did the discretionary overlay (the skips) add or destroy edge,
 * overall and per skip-reason / per strategy.
 */

const MONO = '"Space Mono", monospace';
// Strategy-exit outcomes are neither a save nor a miss on their own — the exit
// fired, and only the SIGN says whether skipping it helped. Amber is the same
// hue tradeDetailPage uses for the non-stopped ghost-verdict marker.
const PURPLE = '#a78bfa'; // de-duped series / folded-row accent

const fmtPct = (p) => (p == null ? '—' : `${p > 0 ? '+' : ''}${p.toFixed(1)}%`);
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');

const STATUS_LABELS = Object.fromEntries(
  Object.entries(SKIP_STATUS_LABELS).map(([status, label]) => [status, label.toUpperCase()])
);

// Chip colouring follows the SKIP's quality (savedR sign), not the trade's:
// a would-be-stopped trade is a GOOD skip.
const verdictFor = (o, { GREEN, RED, NEUTRAL }) => {
  if (o.status === 'would_be_stopped') return { label: 'SAVED −1R', color: GREEN };
  if (o.status === 'would_hit_target') return { label: `MISSED ${fmtR(o.counterfactualR)}`, color: RED };
  if (o.status === 'would_exit_signal') {
    // The strategy's exit graph would have closed it — a RESOLVED result
    // banked at the exit bar's close, not an open mark.
    return o.counterfactualR >= 0
      ? { label: `MISSED ${fmtR(o.counterfactualR)} (EXITED)`, color: RED }
      : { label: `SAVED ${fmtR(-o.counterfactualR)} (EXITED)`, color: GREEN };
  }
  if (o.savedR > 0.1) return { label: `SAVING ${fmtR(o.savedR)}`, color: GREEN };
  if (o.savedR < -0.1) return { label: `COSTING ${fmtR(-o.savedR)}`, color: RED };
  return { label: 'FLAT', color: NEUTRAL };
};

const statLabelSx = {
  fontFamily: MONO,
  fontSize: '0.65rem',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--dwlf-ink-50)',
};

const CounterfactualPage = () => {
  const sc = useSchemeColors(); // the chart is a canvas: it needs resolved colours
  // Semantic hues follow the scheme (the light ones keep contrast on white). Resolved hexes, so the same
  // values serve the canvas chart and the DOM.
  const { accent: CYAN, up: GREEN, down: RED, warn: AMBER } = sc;
  const verdictColours = { GREEN, RED, NEUTRAL: sc.ink(0.5) };
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchSkipOutcomes();
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error?.message || e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const outcomes = useMemo(() => data?.outcomes || [], [data]);
  const aggregates = data?.aggregates;

  // tradeId → symbol, so a folded (de-duped) row can name the position it
  // collapsed into.
  const symbolById = useMemo(
    () => Object.fromEntries(outcomes.map((o) => [o.tradeId, o.symbol])),
    [outcomes]
  );

  const strategyIds = useMemo(
    () => [...new Set(outcomes.map((o) => o.strategyId || 'manual'))],
    [outcomes]
  );

  // A strategy exit is ONE kind of resolution whose sign varies — unlike
  // stopped (always −1R) or target (always positive). So profit/loss belongs as
  // a breakdown INSIDE the bucket, not as two sibling categories. No "neutral"
  // band: an exact 0.00R doesn't occur, and a third tile for a case that never
  // fires reads as missing data rather than as nothing to report.
  // Total and split BOTH come from `outcomes` so the tile can never disagree
  // with the sub-line underneath it. Reading the total from aggregates instead
  // would leave two sources for one number.
  const exitedSplit = useMemo(() => {
    const rows = outcomes.filter((o) => o.status === 'would_exit_signal');
    return {
      total: rows.length,
      inProfit: rows.filter((o) => Number(o.counterfactualR) > 0).length,
      atLoss: rows.filter((o) => Number(o.counterfactualR) < 0).length,
    };
  }, [outcomes]);

  const filtered = useMemo(
    () =>
      strategyFilter === 'all'
        ? outcomes
        : outcomes.filter((o) => (o.strategyId || 'manual') === strategyFilter),
    [outcomes, strategyFilter]
  );

  const filteredNetSavedR = useMemo(
    () => filtered.reduce((acc, o) => acc + (o.savedR || 0), 0),
    [filtered]
  );

  // Decision record over RESOLVED skips only. A running skip has no verdict yet
  // — counting it either way would move the record on nothing more than time
  // passing. Sign of savedR is the verdict: positive means not taking it was
  // the right call.
  // Derived from `outcomes`, NOT `filtered`: this sits in the same card as the
  // bucket tiles, which read unfiltered aggregates. A filtered record among
  // unfiltered totals would compare two different populations side by side and
  // look like an arithmetic error.
  const record = useMemo(() => {
    const resolved = outcomes.filter((o) => o.status !== 'running');
    const good = resolved.filter((o) => Number(o.savedR) > 0).length;
    const bad = resolved.filter((o) => Number(o.savedR) < 0).length;
    const scored = good + bad;
    return {
      good,
      bad,
      resolved: resolved.length,
      open: outcomes.length - resolved.length,
      // Denominator is good+bad, not `resolved`: a null or exactly-zero savedR
      // is resolved but UNSCORED, and leaving it in would quietly drag the
      // percentage down using rows neither tally counted.
      pct: scored ? Math.round((good / scored) * 100) : null,
    };
  }, [outcomes]);

  // Changing the strategy filter reshuffles the list, so a page index carried
  // over from the previous filter can land past the end and render blank.
  useEffect(() => { setPage(0); }, [strategyFilter]);

  const pageRows = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const chart = useMemo(() => {
    const series = aggregates?.series || [];
    const hasDeduped = series.some((p) => Number.isFinite(p.cumulativeSavedRDeduped));
    const datasets = [
      {
        label: 'Raw (every signal)',
        data: series.map((p) => p.cumulativeSavedR),
        borderColor: CYAN,
        backgroundColor: `${CYAN}14`, // ~8% alpha: Chart.js fills on a canvas, which can't read CSS variables
        fill: true,
        stepped: true,
        pointRadius: 3,
        pointBackgroundColor: series.map((p) => (p.savedR >= 0 ? GREEN : RED)),
      },
    ];
    // De-duped curve: flatlines on a folded re-fire (you'd already hold the
    // position). Only added when the API supplies the field.
    if (hasDeduped) {
      datasets.push({
        label: 'De-duped (one position per setup)',
        data: series.map((p) => p.cumulativeSavedRDeduped ?? null), // missing → a gap, not the raw figure
        borderColor: PURPLE,
        backgroundColor: 'transparent',
        fill: false,
        stepped: true,
        borderDash: [6, 4],
        pointRadius: 0,
      });
    }
    return {
      data: {
        labels: series.map((p) => `${fmtDate(p.skippedAt)} ${p.symbol}`),
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            labels: { color: sc.ink(0.6), font: { family: MONO, size: 10 }, boxWidth: 12 },
          },
          tooltip: skipChartTooltip(series, { statusOf: skipStatusLookup(outcomes), hasDeduped }),
        },
        scales: {
          x: { ticks: { color: sc.ink(0.4), font: { family: MONO, size: 9 }, maxRotation: 60 }, grid: { color: sc.ink(0.05) } },
          y: {
            ticks: { color: sc.ink(0.5), font: { family: MONO, size: 10 }, callback: (v) => `${v}R` },
            grid: { color: sc.ink(0.08) },
          },
        },
      },
    };
  }, [aggregates, outcomes, sc]);

  if (loading) return <DWLFLoader />;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box>
          <Typography variant="h4" sx={{ fontFamily: MONO, letterSpacing: '0.04em' }}>
            Counterfactual
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--dwlf-ink-55)', mt: 0.5 }}>
            The trades you didn&apos;t take — every skipped signal replayed against its
            decision-time entry, stop and target. Positive saved-R means the skip was right.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/trades')}
          sx={{
            fontFamily: MONO,
            fontSize: '0.75rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderRadius: 0,
            borderColor: 'var(--dwlf-ink-20)',
            color: 'var(--dwlf-ink-60)',
            '&:hover': { borderColor: CYAN, color: CYAN, backgroundColor: 'transparent' },
          }}
        >
          Trades
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      {!error && aggregates && (
        <>
          {/* Headline stats */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card variant="outlined" sx={{ borderColor: 'var(--dwlf-ink-12)' }}>
                <CardContent>
                  <Typography sx={statLabelSx}>Net R saved by skipping</Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: MONO,
                      color: aggregates.totals.netSavedR >= 0 ? GREEN : RED,
                      mt: 1,
                    }}
                  >
                    {fmtR(aggregates.totals.netSavedR)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--dwlf-ink-45)' }}>
                    raw · across {aggregates.totals.count} skipped signals
                  </Typography>
                  {/* De-duped view: collapses concurrent re-fires of one setup
                      into a single position — what you'd actually have traded. */}
                  {Number.isFinite(aggregates.totals.netSavedRDeduped) && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid var(--dwlf-ink-08)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <Typography
                          variant="h5"
                          sx={{ fontFamily: MONO, color: aggregates.totals.netSavedRDeduped >= 0 ? GREEN : RED }}
                        >
                          {fmtR(aggregates.totals.netSavedRDeduped)}
                        </Typography>
                        <Typography sx={statLabelSx}>de-duped</Typography>
                        <Tooltip
                          arrow
                          placement="top"
                          title="De-duped collapses concurrent re-fires of the SAME setup (same symbol + direction, where the next signal fired while the first's counterfactual position was still open) into one — so it reflects what you'd actually have traded, one position, not the mechanical sum of every signal. Sequential re-fires, where the first resolved before the next fired, stay separate."
                        >
                          <InfoOutlinedIcon sx={{ fontSize: '0.95rem', color: 'var(--dwlf-ink-40)', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'var(--dwlf-ink-45)' }}>
                        {(aggregates.totals.collapsedCount || 0) > 0
                          ? `${aggregates.totals.collapsedCount} concurrent re-fire${aggregates.totals.collapsedCount === 1 ? '' : 's'} collapsed`
                          : 'no concurrent re-fires to collapse'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Card variant="outlined" sx={{ borderColor: 'var(--dwlf-ink-12)', height: '100%' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Would be stopped', value: aggregates.totals.wouldBeStopped, color: GREEN },
                      {
                        label: 'Would have exited',
                        value: exitedSplit.total,
                        color: AMBER,
                        sub: `${exitedSplit.inProfit} in profit · ${exitedSplit.atLoss} at a loss`,
                      },
                      { label: 'Would hit target', value: aggregates.totals.wouldHitTarget, color: RED },
                      { label: 'Still running', value: aggregates.totals.running, color: 'var(--dwlf-ink-80)' },
                    ].map((s) => (
                      <Grid size={{ xs: 6, sm: 3 }} key={s.label}>
                        <Typography sx={statLabelSx}>{s.label}</Typography>
                        <Typography variant="h4" sx={{ fontFamily: MONO, color: s.color, mt: 1 }}>
                          {s.value}
                        </Typography>
                        {s.sub && (
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: MONO, color: 'var(--dwlf-ink-45)', display: 'block' }}
                          >
                            {s.sub}
                          </Typography>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                  <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid var(--dwlf-ink-08)' }}>
                    <Typography sx={statLabelSx}>Decision record</Typography>
                    <Box display="flex" alignItems="baseline" gap={1.5} sx={{ mt: 0.5 }}>
                      <Typography variant="h4" sx={{ fontFamily: MONO }}>
                        <Box component="span" sx={{ color: GREEN }}>{record.good}</Box>
                        <Box component="span" sx={{ color: 'var(--dwlf-ink-30)' }}>–</Box>
                        <Box component="span" sx={{ color: RED }}>{record.bad}</Box>
                      </Typography>
                      {record.pct !== null && (
                        <Typography variant="body2" sx={{ fontFamily: MONO, color: 'var(--dwlf-ink-60)' }}>
                          {record.pct}% of resolved skips were the right call
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: 'var(--dwlf-ink-40)', display: 'block', mt: 0.5 }}>
                      {record.resolved} resolved · {record.open} still open, which carry no verdict
                      yet. A skip counts as right when the trade it passed on would have lost. Read
                      this alongside the R rather than instead of it — every stopped skip scores
                      exactly +1R while strategy exits land much smaller either way, so the record
                      and the R need not move together.
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'var(--dwlf-ink-40)', display: 'block', mt: 2 }}>
                    Mechanical replay of the planned levels, plus the strategy's own exit graph —
                    no discretionary management. Stopped outcomes are capped at exactly −1R;
                    exited outcomes close at the exit bar's close, so they carry either sign;
                    running outcomes are open marks. Peak / Worst = the most the trade ever
                    offered / the worst it ever got, in R, since the signal (max favourable /
                    adverse excursion).
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Typography variant="caption" sx={{ color: 'var(--dwlf-ink-45)', display: 'block', mt: 1 }}>
            Hypothetical replay of your own skips — not actual results, not a forecast.
          </Typography>

          {/* Cumulative saved-R */}
          {(aggregates.series || []).length > 1 && (
            <Paper variant="outlined" sx={{ mt: 3, p: 2, borderColor: 'var(--dwlf-ink-12)' }}>
              <Typography sx={statLabelSx}>Cumulative R saved (skip order)</Typography>
              <Box sx={{ height: { xs: 340, md: 440 }, mt: 1 }}>
                <Line data={chart.data} options={chart.options} />
              </Box>
            </Paper>
          )}

          {/* Per-reason replayed outcome */}
          <Paper variant="outlined" sx={{ mt: 3, p: 2, borderColor: 'var(--dwlf-ink-12)' }}>
            <Typography sx={statLabelSx}>Replayed outcome by skip reason</Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              {Object.entries(aggregates.byReason)
                .sort((a, b) => b[1].netSavedR - a[1].netSavedR)
                .map(([reason, v]) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={reason}>
                    <Box
                      sx={{
                        border: '1px solid var(--dwlf-ink-10)',
                        p: 1.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ color: 'var(--dwlf-ink-85)' }}>
                          {getSkipReasonLabel(reason)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--dwlf-ink-45)' }}>
                          {v.count} skip{v.count === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{ fontFamily: MONO, color: v.netSavedR >= 0 ? GREEN : RED, whiteSpace: 'nowrap' }}
                      >
                        {fmtR(v.netSavedR)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
            </Grid>
            <Typography variant="caption" sx={{ color: 'var(--dwlf-ink-40)', display: 'block', mt: 1.5 }}>
              Multi-reason skips count toward each of their reasons, so buckets can overlap.
            </Typography>
          </Paper>

          {/* Outcomes table */}
          <Paper variant="outlined" sx={{ mt: 3, p: 2, borderColor: 'var(--dwlf-ink-12)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Typography sx={statLabelSx}>
                Skipped signals
                {strategyFilter !== 'all' && ` — net ${fmtR(filteredNetSavedR)} for this strategy`}
              </Typography>
              {strategyIds.length > 1 && (
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel sx={{ fontFamily: MONO, fontSize: '0.75rem' }}>Strategy</InputLabel>
                  <Select
                    value={strategyFilter}
                    label="Strategy"
                    onChange={(e) => setStrategyFilter(e.target.value)}
                    sx={{ fontFamily: MONO, fontSize: '0.8rem' }}
                  >
                    <MenuItem value="all">All strategies</MenuItem>
                    {strategyIds.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {/* Verdict sits directly after Reasons: it is the answer to the
                        question the row asks, and trailing it behind six numeric
                        columns put it off-screen behind a horizontal scroll. */}
                    {['Symbol', 'Skipped', 'Reasons', 'Verdict', 'Entry', 'Now', 'Move', 'R', 'Peak', 'Worst'].map((h) => (
                      <TableCell key={h} sx={{ ...statLabelSx, borderColor: 'var(--dwlf-ink-10)' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((o) => {
                    const verdict = verdictFor(o, verdictColours);
                    return (
                      <TableRow key={o.tradeId} hover sx={o.duplicateOf ? { opacity: 0.5 } : undefined}>
                        <TableCell sx={{ fontFamily: MONO, color: 'var(--dwlf-ink-90)' }}>
                          {o.symbol}
                          {/* Folded into an earlier still-open position of the same
                              setup — excluded from the de-duped net. */}
                          {o.duplicateOf && (
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', color: PURPLE, fontSize: '0.6rem', letterSpacing: '0.04em' }}
                            >
                              ↳ folded{symbolById[o.duplicateOf] ? ` into ${symbolById[o.duplicateOf]}` : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: MONO, color: 'var(--dwlf-ink-60)' }}>
                          {fmtDate(o.skippedAt)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={o.skipNote || ''} placement="top-start">
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {/* Backend normalises legacy single skipReason into this
                                  array (never empty) — the || [] is pure belt-and-braces. */}
                              {(o.skipReasons || []).map((r) => (
                                <Chip
                                  key={r}
                                  label={getSkipReasonLabel(r)}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.65rem',
                                    borderColor: 'var(--dwlf-ink-20)',
                                    color: 'var(--dwlf-ink-70)',
                                  }}
                                />
                              ))}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${verdict.label}${o.status === 'running' ? '' : ` · ${STATUS_LABELS[o.status]}`}`}
                            size="small"
                            sx={{
                              fontFamily: MONO,
                              fontSize: '0.62rem',
                              letterSpacing: '0.05em',
                              color: verdict.color,
                              border: `1px solid ${verdict.color}`,
                              backgroundColor: 'transparent',
                              whiteSpace: 'nowrap',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: MONO, color: 'var(--dwlf-ink-70)' }}>
                          {o.entryPrice}
                        </TableCell>
                        <TableCell sx={{ fontFamily: MONO, color: 'var(--dwlf-ink-70)' }}>
                          {o.currentPrice ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontFamily: MONO, color: (o.movePct || 0) >= 0 ? GREEN : RED }}>
                          {fmtPct(o.movePct)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: MONO, color: o.counterfactualR >= 0 ? GREEN : RED }}>
                          {fmtR(o.counterfactualR)}
                        </TableCell>
                        {/* Max favourable / adverse excursion since the signal — the honest
                            "how much did it ever offer / how bad did it ever get" pair. */}
                        <TableCell sx={{ fontFamily: MONO, color: `${GREEN}A6` }}>
                          {fmtR(o.mfeR)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: MONO, color: `${RED}A6` }}>
                          {fmtR(o.maeR)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {/* The skip journal only grows — rendering every row was fine at 40
                and will not be at 400. Hidden when empty: "1–0 of 0" next to the
                no-results message reads as a loading failure. */}
            {filtered.length > 0 && (
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
              sx={{
                color: 'var(--dwlf-ink-60)',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontFamily: MONO,
                  fontSize: '0.7rem',
                },
              }}
            />
            )}
            {filtered.length === 0 && (
              <Typography variant="body2" sx={{ color: 'var(--dwlf-ink-50)', mt: 2 }}>
                No skipped trades yet — skip a planned signal from the review screen and it will
                show up here with its counterfactual.
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};

export default CounterfactualPage;
